"use client";

import { useState, useRef, type FormEvent } from "react";
import Script from "next/script";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: { sitekey: string; callback?: (token: string) => void }
      ) => string;
      getResponse: (widgetId?: string) => string | undefined;
      reset: (widgetId?: string) => void;
    };
  }
}


/* ───── Icon components ───── */
function IconArrowRight(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function IconPhone(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function IconMail(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 7l-10 7L2 7" />
    </svg>
  );
}

function IconMapPin(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

/* ───── PAGE ───── */
export default function ContactoPage() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | undefined>(undefined);

  function renderTurnstile() {
    if (turnstileContainerRef.current && window.turnstile && !turnstileWidgetId.current) {
      turnstileWidgetId.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
      });
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");

    const turnstileToken = window.turnstile?.getResponse(turnstileWidgetId.current);
    if (!turnstileToken) {
      setError("Completa a verificación anti-spam antes de enviar.");
      return;
    }

    setSending(true);

    const form = e.currentTarget;
    const data = {
      nome: (form.elements.namedItem("nome") as HTMLInputElement).value,
      apelidos: (form.elements.namedItem("apelidos") as HTMLInputElement).value,
      email: (form.elements.namedItem("email") as HTMLInputElement).value,
      telefono: (form.elements.namedItem("telefono") as HTMLInputElement).value,
      enderezo: (form.elements.namedItem("enderezo") as HTMLInputElement).value,
      horario: (form.elements.namedItem("horario") as HTMLSelectElement).value,
      motivo: (form.elements.namedItem("motivo") as HTMLSelectElement).value,
      comentarios: (form.elements.namedItem("comentarios") as HTMLTextAreaElement).value,
      empresa: (form.elements.namedItem("empresa") as HTMLInputElement).value,
      turnstileToken,
    };

    try {
      const res = await fetch("/api/contacto", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (json.ok) {
        setSent(true);
      } else {
        setError(json.error || "Houbo un erro ao enviar o formulario. Inténtao de novo.");
      }
    } catch {
      setError("Non se puido conectar co servidor. Inténtao de novo.");
    } finally {
      setSending(false);
      window.turnstile?.reset(turnstileWidgetId.current);
    }
  }

  const inputClass =
    "w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-[var(--navy)] placeholder:text-gray-400 focus:border-[var(--blue)] focus:outline-none focus:ring-2 focus:ring-[var(--blue)]/20 transition";

  return (
    <>
      {/* ====== NAVBAR ====== */}
      <Navbar active="Contacto" />

      {/* ====== HERO ====== */}
      <section className="relative flex min-h-[40vh] items-center overflow-hidden bg-[var(--navy)] pt-20">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-[var(--blue)]/10" />
          <div className="absolute bottom-10 left-10 h-4 w-4 rounded-full bg-[var(--blue)]/40" />
          <div className="absolute top-40 left-[15%] h-3 w-3 rounded-full bg-[var(--blue)]/30" />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 py-16">
          <div className="flex flex-col justify-center max-w-2xl">
            <span className="mb-4 inline-block w-fit rounded-full bg-[var(--blue)]/15 px-4 py-1.5 text-sm font-medium text-[var(--blue)]">
              Contacto
            </span>
            <h1 className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl">
              Falamos?
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-gray-300">
              Contacta connosco e teremos a mellor solución para ti.
              Sen sorpresas, sen letras pequenas.
            </p>
          </div>
        </div>
      </section>

      {/* ====== CONTACT INFO + FORM ====== */}
      <section id="formulario" className="scroll-mt-20 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-8 lg:gap-16 lg:grid-cols-5">
            {/* Info column */}
            <div className="lg:col-span-2 space-y-10">
              <div>
                <h2 className="text-2xl font-bold text-[var(--navy)]">Datos de contacto</h2>
                <p className="mt-2 text-sm text-gray-500">
                  Estamos preto de ti para ofrecerche o mellor soporte.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--navy)]/5 text-[var(--navy)]">
                    <IconPhone className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[var(--navy)]">Teléfono</p>
                    <a href="tel:+34986570997" className="text-sm text-gray-500 hover:text-[var(--blue)] transition">
                      986 570 997
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--navy)]/5 text-[var(--navy)]">
                    <IconMail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[var(--navy)]">Email</p>
                    <a href="mailto:soporte@aestradadixital.gal" className="text-sm text-gray-500 hover:text-[var(--blue)] transition">
                      soporte@aestradadixital.gal
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--navy)]/5 text-[var(--navy)]">
                    <IconMapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[var(--navy)]">Oficina</p>
                    <p className="text-sm text-gray-500">
                      A Estrada, Pontevedra
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2940.5!2d-8.485!3d42.687!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd2f0c!2sAESTRADADIXITAL!5e0!3m2!1sgl!2ses!4v1"
                  width="100%"
                  height="220"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localización de A Estrada Dixital"
                />
              </div>
            </div>

            {/* Form column */}
            <div className="lg:col-span-3">
              <div className="rounded-2xl bg-gray-50 p-8 sm:p-10">
                <h2 className="text-2xl font-bold text-[var(--navy)]">Envíanos unha mensaxe</h2>
                <p className="mt-2 text-sm text-gray-500">
                  Cubre o formulario e contactaremos contigo o antes posible.
                </p>

                {sent ? (
                  <div className="mt-10 rounded-xl bg-[var(--green)]/10 p-8 text-center">
                    <p className="text-lg font-semibold text-[var(--navy)]">Mensaxe enviada!</p>
                    <p className="mt-2 text-sm text-gray-500">
                      Grazas por contactar connosco. Responderemosche o antes posible.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                    {/* Honeypot anti-bot: oculto para persoas reais, os bots adoitan recheало igualmente */}
                    <input
                      type="text"
                      name="empresa"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden"
                    />
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="nome" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--navy)]/60">
                          Nome *
                        </label>
                        <input id="nome" name="nome" type="text" required className={inputClass} placeholder="O teu nome" />
                      </div>
                      <div>
                        <label htmlFor="apelidos" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--navy)]/60">
                          Apelidos *
                        </label>
                        <input id="apelidos" name="apelidos" type="text" required className={inputClass} placeholder="Os teus apelidos" />
                      </div>
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--navy)]/60">
                          Email *
                        </label>
                        <input id="email" name="email" type="email" required className={inputClass} placeholder="email@exemplo.gal" />
                      </div>
                      <div>
                        <label htmlFor="telefono" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--navy)]/60">
                          Teléfono *
                        </label>
                        <input id="telefono" name="telefono" type="tel" required className={inputClass} placeholder="600 000 000" />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="enderezo" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--navy)]/60">
                        Enderezo
                      </label>
                      <input id="enderezo" name="enderezo" type="text" className={inputClass} placeholder="Rúa, número, localidade" />
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="horario" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--navy)]/60">
                          Horario preferido
                        </label>
                        <select id="horario" name="horario" className={inputClass}>
                          <option value="">Seleccionar...</option>
                          <option value="manana">Pola mañá</option>
                          <option value="tarde">Pola tarde</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="motivo" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--navy)]/60">
                          Motivo da consulta
                        </label>
                        <select id="motivo" name="motivo" className={inputClass}>
                          <option value="">Seleccionar...</option>
                          <option value="wifi">WiFi</option>
                          <option value="fibra">Fibra</option>
                          <option value="telefonia">Telefonía</option>
                          <option value="empresas">Empresas</option>
                          <option value="outro">Outro</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="comentarios" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--navy)]/60">
                        Comentarios ou incidencias
                      </label>
                      <textarea
                        id="comentarios"
                        name="comentarios"
                        rows={4}
                        className={inputClass}
                        placeholder="Cóntanos en que podemos axudarche..."
                      />
                    </div>

                    <div ref={turnstileContainerRef} />

                    {error && (
                      <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
                        {error}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={sending}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--navy)] px-8 py-4 text-sm font-semibold text-white transition hover:bg-[var(--navy-light)] disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
                    >
                      {sending ? "Enviando..." : "Enviar mensaxe"}
                      {!sending && <IconArrowRight className="h-4 w-4" />}
                    </button>
                  </form>
                )}

                <Script
                  src="https://challenges.cloudflare.com/turnstile/v0/api.js"
                  strategy="afterInteractive"
                  onLoad={renderTurnstile}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
